/**
 * Subscription detail with inline editing across three independent sections:
 *   A — subscription card (name, description, owner, cadence, active window).
 *   B — deliveries (add/remove/reorder, name + subject + body + content
 *       selection).
 *   C — recipients per delivery (a person or a group, picked from the active list).
 *
 * Each section has its own Save button and dirty-tracks against a JSON
 * snapshot. The target — the page or the Atlas Report (ADR-0216) — is the
 * anchor and is never editable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { SubscriptionVariablesHelp } from "../components/SubscriptionVariablesHelp.js";
import { deliverySummaryText, notSentNote } from "../lib/subscriptionRunSummary.js";
import {
  RecipientsEditor,
  recipientToWire,
  recipientsAreComplete,
  type Candidate,
  type DraftRecipient,
} from "../components/SubscriptionRecipientsEditor.js";

type RecipientKind = "account" | "group" | "external_email";

type Recipient = {
  id: string;
  kind: RecipientKind;
  display_name: string;
  email_hint: string | null;
  account_id: string | null;
  group_id: string | null;
  external_email: string | null;
  not_receiving: string | null;
};

type Delivery = {
  id: string;
  name: string;
  content_selection: { kind: "whole_page" } | { kind: "widgets"; widget_ids: string[] } | { kind: "whole_report" };
  subject_template: string;
  body_template: string | null;
  sort_order: number;
  recipients: Recipient[];
};

type Run = {
  id: string;
  fired_at: string;
  completed_at: string | null;
  status:
    | "running"
    | "succeeded"
    | "partial_failure"
    | "failed"
    | "cancelled"
    | "no_recipients";
  refreshed_before_send: string;
  trigger: "schedule" | "manual";
  render_error: string | null;
  email_sent_count: number;
  email_failed_count: number;
  email_suppressed_count: number;
  email_skipped_access_count?: number;
  skipped_access_names?: string[];
};

type SubscriptionDetail = {
  subscription: {
    id: string;
    tenant_id: string;
    name: string;
    description: string | null;
    page_id: string | null;
    page_title: string | null;
    /** ADR-0216: what it delivers — a page or an Atlas Report. */
    target: { kind: "page" | "atlas_report"; id: string; title: string };
    owner_account_id: string;
    owner_display_name: string;
    cadence: Cadence;
    cadence_config: Record<string, unknown>;
    active_from: string | null;
    active_until: string | null;
    paused: boolean;
    last_fired_at: string | null;
    next_fire_at: string | null;
  };
  deliveries: Delivery[];
  recent_runs: Run[];
};

type Cadence = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

const WEEKDAYS: Array<{ code: string; label: string }> = [
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
  { code: "sun", label: "Sun" },
];

type PageWidget = { id: string; title: string; kind: string; parent_id: string | null };

// ---------- helpers --------------------------------------------------------

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function runStatusPill(status: Run["status"]): JSX.Element {
  if (status === "succeeded") return <span className="pill pillOk">✓ succeeded</span>;
  if (status === "partial_failure") return <span className="pill pillWarn">⚠ partial</span>;
  if (status === "failed") return <span className="pill pillBad">✗ failed</span>;
  if (status === "running") return <span className="pill">⟳ running</span>;
  if (status === "no_recipients") return <span className="pill pillWarn">⚠ no recipients</span>;
  return <span className="pill">{status}</span>;
}

/** Compact delivery tally for a run (lib/subscriptionRunSummary.ts). */
function deliverySummary(run: Run): JSX.Element {
  const text = deliverySummaryText(run);
  // Only flag zero-sent on a run that actually attempted delivery — a running
  // or cancelled run hasn't earned the warning.
  const attemptedDelivery = run.status !== "running" && run.status !== "cancelled";
  return attemptedDelivery && run.email_sent_count === 0 ? (
    <span className="warnText">{text}</span>
  ) : (
    <span>{text}</span>
  );
}

function buildCadenceConfig(
  cadence: Cadence,
  time: string,
  weeklyDays: string[],
  monthlyDay: string,
  yearlyMonth: number,
  yearlyDay: number,
): Record<string, unknown> {
  switch (cadence) {
    case "DAILY":
      return { time };
    case "WEEKLY":
      return { time, days: weeklyDays };
    case "MONTHLY":
      return monthlyDay === "LAST" ? { time, day: "LAST" } : { time, day: Number.parseInt(monthlyDay, 10) };
    case "YEARLY":
      return { time, month: yearlyMonth, day: yearlyDay };
  }
}

function readCadenceFields(
  cadence: Cadence,
  cfg: Record<string, unknown>,
): {
  time: string;
  weeklyDays: string[];
  monthlyDay: string;
  yearlyMonth: number;
  yearlyDay: number;
} {
  return {
    time: typeof cfg.time === "string" ? cfg.time : "07:00",
    weeklyDays:
      Array.isArray(cfg.days) && cadence === "WEEKLY" ? (cfg.days as string[]) : ["mon"],
    monthlyDay:
      cadence === "MONTHLY"
        ? cfg.day === "LAST"
          ? "LAST"
          : typeof cfg.day === "number"
            ? String(cfg.day)
            : "1"
        : "1",
    yearlyMonth: typeof cfg.month === "number" && cadence === "YEARLY" ? cfg.month : 1,
    yearlyDay: typeof cfg.day === "number" && cadence === "YEARLY" ? cfg.day : 15,
  };
}

// ---------- types for editable drafts -------------------------------------

type DraftDelivery = {
  id?: string;
  name: string;
  contentKind: "whole_page" | "widgets" | "whole_report";
  widgetIds: string[];
  subjectTemplate: string;
  bodyTemplate: string;
  recipients: DraftRecipient[];
};

function deliveryToDraft(d: Delivery): DraftDelivery {
  return {
    id: d.id,
    name: d.name,
    contentKind: d.content_selection.kind,
    widgetIds:
      d.content_selection.kind === "widgets" ? [...d.content_selection.widget_ids] : [],
    subjectTemplate: d.subject_template,
    bodyTemplate: d.body_template ?? "",
    recipients: d.recipients.map<DraftRecipient>((r) => {
      if (r.kind === "external_email") {
        return {
          id: r.id,
          kind: "external_email",
          externalEmail: r.external_email ?? r.display_name,
          notReceiving: r.not_receiving,
        };
      }
      if (r.kind === "group") {
        return {
          id: r.id,
          kind: "group",
          groupId: r.group_id ?? "",
          displayName: r.display_name,
          notReceiving: r.not_receiving,
        };
      }
      return {
        id: r.id,
        kind: "account",
        accountId: r.account_id ?? "",
        displayName: r.display_name,
        notReceiving: r.not_receiving,
      };
    }),
  };
}

// ===========================================================================
// Main route component
// ===========================================================================

export function TenantSubscriptionDetail(props: { entitlement: string }): JSX.Element | null {
  const { entitlement } = props;
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [detail, setDetail] = useState<SubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"settings" | "runs">("settings");
  const [runs, setRuns] = useState<Run[]>([]);
  const [busy, setBusy] = useState(false);
  const pollTimerRef = useRef<number | null>(null);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [widgets, setWidgets] = useState<PageWidget[]>([]);

  const load = useCallback(async (): Promise<SubscriptionDetail | null> => {
    try {
      const data = await apiFetch<SubscriptionDetail>(`/api/tenant/subscriptions/${id}`);
      setDetail(data);
      setRuns(data.recent_runs ?? []);
      setError(null);
      return data;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [id]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ candidates: Candidate[] }>("/api/tenant/rasci-candidates");
        setCandidates(data.candidates ?? []);
      } catch {
        // Picker will fall back to typed input; not fatal.
      }
    })();
  }, []);

  useEffect(() => {
    if (!detail || detail.subscription.target.kind !== "page") return;
    void (async () => {
      try {
        const data = await apiFetch<{ published: boolean; widgets: PageWidget[] }>(
          `/api/tenant/subscriptions/page-widgets/${encodeURIComponent(detail.subscription.target.id)}`,
        );
        setWidgets(data.widgets ?? []);
      } catch {
        setWidgets([]);
      }
    })();
  }, [detail?.subscription.target.kind, detail?.subscription.target.id]);

  const refreshRuns = useCallback(async () => {
    try {
      const data = await apiFetch<{ items: Run[] }>(
        `/api/tenant/subscriptions/${id}/runs?limit=20`,
      );
      setRuns(data.items ?? []);
    } catch {
      // best-effort polling
    }
  }, [id]);

  useEffect(() => {
    if (tab !== "runs") return;
    const anyRunning = runs.some((r) => r.status === "running");
    if (!anyRunning) {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    pollTimerRef.current = window.setInterval(() => {
      void refreshRuns();
    }, 5000);
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [tab, runs, refreshRuns]);

  const runNow = useCallback(async () => {
    setBusy(true);
    try {
      await apiFetch<{ ok: boolean }>(`/api/tenant/subscriptions/${id}/run-now`, { method: "POST" });
      toast.push({ kind: "success", message: "Run started. Watch the Run history tab for status." });
      setTab("runs");
      window.setTimeout(() => void refreshRuns(), 1500);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }, [id, toast, refreshRuns]);

  const togglePause = useCallback(async () => {
    if (!detail) return;
    const path = detail.subscription.paused ? "resume" : "pause";
    setBusy(true);
    try {
      await apiFetch<{ ok: boolean }>(`/api/tenant/subscriptions/${id}/${path}`, { method: "POST" });
      await load();
      toast.push({
        kind: "success",
        message: detail.subscription.paused ? "Subscription resumed." : "Subscription paused.",
      });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }, [detail, id, load, toast]);

  const deleteSub = useCallback(async () => {
    if (!detail) return;
    const ok = await confirm.confirm({
      title: "Delete subscription?",
      detail: `This permanently deletes "${detail.subscription.name}" and all its run history.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch<void>(`/api/tenant/subscriptions/${id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Subscription deleted." });
      nav("/app/schedule/subscriptions");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }, [detail, id, confirm, nav, toast]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState detail={error} />;
  if (!detail) return null;

  const sub = detail.subscription;

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <Link className="breadcrumbLink" to="/app/schedule/subscriptions">
            Subscriptions
          </Link>
          <span className="breadcrumbSep">/</span>
          <span>{sub.name}</span>
        </span>
      }
      actions={
        <>
          <button type="button" className="btn" disabled={busy || sub.paused} onClick={() => void runNow()}>
            {busy ? "…" : "Run now"}
          </button>
          <button
            type="button"
            className={sub.paused ? "btn btnPrimary" : "btn"}
            disabled={busy}
            onClick={() => void togglePause()}
          >
            {sub.paused ? "Resume" : "Pause"}
          </button>
          <button type="button" className="btn btnDanger" disabled={busy} onClick={() => void deleteSub()}>
            Delete
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {/* Above-the-fold metadata strip */}
        <div className="card" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>Target</div>
            {sub.target.kind === "atlas_report" ? (
              <div>
                <Link to={`/app/workspace/atlas-reports/${encodeURIComponent(sub.target.id)}`}>{sub.target.title}</Link>{" "}
                <span className="pill pillNeutral">report</span>
              </div>
            ) : (
              <Link to={`/app/build/pages/${encodeURIComponent(sub.target.id)}`}>{sub.target.title}</Link>
            )}
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>Status</div>
            <div>
              {sub.paused ? <span className="pill pillWarn">Paused</span> : <span className="pill pillOk">Active</span>}
            </div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>Last fired</div>
            <div>{fmtTime(sub.last_fired_at)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>Next fire</div>
            <div>{fmtTime(sub.next_fire_at)}</div>
          </div>
        </div>

        <div className="card" style={{ background: "var(--panel, #f8fafc)" }}>
          <span className="muted" style={{ fontSize: 13 }}>
            In-flight runs use the configuration they started with. Edits take effect on the next scheduled fire.
            What this subscription delivers is its anchor — to send a different page or report, create a new subscription.
          </span>
        </div>

        {/* Tabs */}
        <div className="tabs" role="tablist" aria-label="Subscription detail tabs">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "settings"}
            className={`tab${tab === "settings" ? " tabActive" : ""}`}
            onClick={() => setTab("settings")}
          >
            Settings
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "runs"}
            className={`tab${tab === "runs" ? " tabActive" : ""}`}
            onClick={() => {
              setTab("runs");
              void refreshRuns();
            }}
          >
            Run history
          </button>
        </div>

        {tab === "settings" ? (
          <>
            <SubscriptionCardEditor
              detail={detail}
              candidates={candidates}
              isAdmin={entitlement === "admin"}
              onSaved={async () => {
                await load();
                toast.push({ kind: "success", message: "Subscription updated." });
              }}
            />
            <DeliveriesEditor
              detail={detail}
              widgets={widgets}
              candidates={candidates}
              onSaved={async () => {
                await load();
                toast.push({ kind: "success", message: "Deliveries updated." });
              }}
            />
          </>
        ) : null}

        {tab === "runs" ? (
          <div className="card">
            <h3>Run history</h3>
            {runs.length === 0 ? (
              <div className="muted">
                No runs yet. Click <strong>Run now</strong> to fire one.
              </div>
            ) : (
              <div className="tableWrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Fired at</th>
                      <th>Trigger</th>
                      <th>Status</th>
                      <th>Delivery</th>
                      <th>Refresh outcome</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.id}>
                        <td>{fmtTime(r.fired_at)}</td>
                        <td>{r.trigger}</td>
                        <td>{runStatusPill(r.status)}</td>
                        <td>{deliverySummary(r)}</td>
                        <td>{r.refreshed_before_send}</td>
                        <td className="muted" style={{ maxWidth: 400, whiteSpace: "pre-wrap" }}>
                          {[notSentNote(r), r.render_error].filter(Boolean).join("\n")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}

// ===========================================================================
// Section A — subscription card editor
// ===========================================================================

function SubscriptionCardEditor(props: {
  detail: SubscriptionDetail;
  candidates: Candidate[];
  isAdmin: boolean;
  onSaved: () => Promise<void> | void;
}): JSX.Element {
  const { detail, candidates, isAdmin, onSaved } = props;
  const sub = detail.subscription;
  const toast = useToast();

  const initialCadenceFields = useMemo(
    () => readCadenceFields(sub.cadence, sub.cadence_config ?? {}),
    [sub.cadence, sub.cadence_config],
  );

  const [name, setName] = useState(sub.name);
  const [description, setDescription] = useState(sub.description ?? "");
  const [ownerAccountId, setOwnerAccountId] = useState(sub.owner_account_id);
  const [cadence, setCadence] = useState<Cadence>(sub.cadence);
  const [time, setTime] = useState(initialCadenceFields.time);
  const [weeklyDays, setWeeklyDays] = useState<string[]>(initialCadenceFields.weeklyDays);
  const [monthlyDay, setMonthlyDay] = useState(initialCadenceFields.monthlyDay);
  const [yearlyMonth, setYearlyMonth] = useState(initialCadenceFields.yearlyMonth);
  const [yearlyDay, setYearlyDay] = useState(initialCadenceFields.yearlyDay);
  const [activeFrom, setActiveFrom] = useState(sub.active_from ? sub.active_from.slice(0, 10) : "");
  const [activeUntil, setActiveUntil] = useState(sub.active_until ? sub.active_until.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);

  const userCandidates = candidates.filter((c) => c.kind === "user");
  // If the current owner isn't in candidates (e.g. contact-typed owner from an
  // older row, or list endpoint excluded them), keep them in the dropdown so
  // the value renders rather than going blank.
  const ownerInList = userCandidates.some((c) => c.id === ownerAccountId);
  const ownerOptions = ownerInList
    ? userCandidates
    : [
        { id: ownerAccountId, kind: "user" as const, display_name: sub.owner_display_name },
        ...userCandidates,
      ];

  const snapshot = useMemo(
    () =>
      JSON.stringify({
        name: sub.name,
        description: sub.description ?? "",
        ownerAccountId: sub.owner_account_id,
        cadence: sub.cadence,
        cadence_config: sub.cadence_config ?? {},
        active_from: sub.active_from ?? "",
        active_until: sub.active_until ?? "",
      }),
    [sub],
  );
  const current = JSON.stringify({
    name,
    description,
    ownerAccountId,
    cadence,
    cadence_config: buildCadenceConfig(cadence, time, weeklyDays, monthlyDay, yearlyMonth, yearlyDay),
    active_from: activeFrom,
    active_until: activeUntil,
  });
  const dirty = current !== snapshot;

  const reset = useCallback(() => {
    setName(sub.name);
    setDescription(sub.description ?? "");
    setOwnerAccountId(sub.owner_account_id);
    setCadence(sub.cadence);
    const f = readCadenceFields(sub.cadence, sub.cadence_config ?? {});
    setTime(f.time);
    setWeeklyDays(f.weeklyDays);
    setMonthlyDay(f.monthlyDay);
    setYearlyMonth(f.yearlyMonth);
    setYearlyDay(f.yearlyDay);
    setActiveFrom(sub.active_from ? sub.active_from.slice(0, 10) : "");
    setActiveUntil(sub.active_until ? sub.active_until.slice(0, 10) : "");
  }, [sub]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        cadence,
        cadence_config: buildCadenceConfig(cadence, time, weeklyDays, monthlyDay, yearlyMonth, yearlyDay),
        active_from: activeFrom || null,
        active_until: activeUntil || null,
      };
      if (ownerAccountId !== sub.owner_account_id) {
        body.owner_account_id = ownerAccountId;
      }
      await apiFetch<{ ok: boolean }>(`/api/tenant/subscriptions/${sub.id}`, {
        method: "PATCH",
        body,
      });
      await onSaved();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }, [
    sub.id,
    sub.owner_account_id,
    name,
    description,
    ownerAccountId,
    cadence,
    time,
    weeklyDays,
    monthlyDay,
    yearlyMonth,
    yearlyDay,
    activeFrom,
    activeUntil,
    onSaved,
    toast,
  ]);

  return (
    <div className="card">
      <h3>Subscription</h3>
      <div style={{ display: "grid", gap: 10 }}>
        <label className="field">
          <span className="label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Description</span>
          <textarea
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Owner</span>
          <select
            className="select"
            value={ownerAccountId}
            onChange={(e) => setOwnerAccountId(e.target.value)}
            disabled={!isAdmin}
          >
            {ownerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
          <div className="help">
            {isAdmin
              ? "Owner transfers take effect on the next scheduled fire."
              : "Only tenant admins can change the owner."}
          </div>
        </label>
        <div className="field">
          <span className="label">Cadence</span>
          <div style={{ display: "flex", gap: 8 }}>
            {(["DAILY", "WEEKLY", "MONTHLY", "YEARLY"] as Cadence[]).map((c) => (
              <label key={c} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input type="radio" name={`cadence-${sub.id}`} checked={cadence === c} onChange={() => setCadence(c)} />
                <span>{c.charAt(0) + c.slice(1).toLowerCase()}</span>
              </label>
            ))}
          </div>
          <div className="help">
            Cadence and active-window changes cancel any pending fire and reschedule from now.
          </div>
        </div>
        <label className="field">
          <span className="label">Time (HH:MM, tenant timezone)</span>
          <input
            className="input"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            placeholder="07:00"
            style={{ maxWidth: 120 }}
          />
        </label>
        {cadence === "WEEKLY" ? (
          <div className="field">
            <span className="label">Days of the week</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {WEEKDAYS.map((d) => (
                <label key={d.code} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={weeklyDays.includes(d.code)}
                    onChange={(e) =>
                      setWeeklyDays((prev) =>
                        e.target.checked ? [...prev, d.code] : prev.filter((x) => x !== d.code),
                      )
                    }
                  />
                  {d.label}
                </label>
              ))}
            </div>
          </div>
        ) : null}
        {cadence === "MONTHLY" ? (
          <label className="field">
            <span className="label">Day of month</span>
            <select className="select" value={monthlyDay} onChange={(e) => setMonthlyDay(e.target.value)}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
              <option value="LAST">Last day</option>
            </select>
          </label>
        ) : null}
        {cadence === "YEARLY" ? (
          <div style={{ display: "flex", gap: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="label">Month</span>
              <input
                className="input"
                type="number"
                min={1}
                max={12}
                value={yearlyMonth}
                onChange={(e) => setYearlyMonth(Number.parseInt(e.target.value, 10) || 1)}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="label">Day</span>
              <input
                className="input"
                type="number"
                min={1}
                max={28}
                value={yearlyDay}
                onChange={(e) => setYearlyDay(Number.parseInt(e.target.value, 10) || 1)}
              />
            </label>
          </div>
        ) : null}
        <div style={{ display: "flex", gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Active from</span>
            <input
              className="input"
              type="date"
              value={activeFrom}
              onChange={(e) => setActiveFrom(e.target.value)}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Active until</span>
            <input
              className="input"
              type="date"
              value={activeUntil}
              onChange={(e) => setActiveUntil(e.target.value)}
            />
          </label>
        </div>
      </div>
      <div className="actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn btnPrimary"
          disabled={!dirty || saving || !name.trim()}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button type="button" className="btn" disabled={!dirty || saving} onClick={reset}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Section B — deliveries editor (with recipients section C nested)
// ===========================================================================

function DeliveriesEditor(props: {
  detail: SubscriptionDetail;
  widgets: PageWidget[];
  candidates: Candidate[];
  onSaved: () => Promise<void> | void;
}): JSX.Element {
  const { detail, widgets, candidates, onSaved } = props;
  const toast = useToast();
  const confirm = useConfirm();

  const initial = useMemo<DraftDelivery[]>(
    () => detail.deliveries.map((d) => deliveryToDraft(d)),
    [detail.deliveries],
  );

  const [drafts, setDrafts] = useState<DraftDelivery[]>(initial);
  const [expanded, setExpanded] = useState<number | null>(initial.length === 1 ? 0 : null);
  const [saving, setSaving] = useState(false);

  // Keep drafts in sync if parent reloads
  useEffect(() => {
    setDrafts(initial);
  }, [initial]);

  const snapshot = JSON.stringify(initial);
  const current = JSON.stringify(drafts);
  const dirty = snapshot !== current;

  function patchDraft(idx: number, p: Partial<DraftDelivery>): void {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...p } : d)));
  }

  function moveUp(idx: number): void {
    if (idx === 0) return;
    setDrafts((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    setExpanded(idx - 1);
  }

  function moveDown(idx: number): void {
    setDrafts((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
    setExpanded(idx + 1);
  }

  async function removeDelivery(idx: number): Promise<void> {
    const ok = await confirm.confirm({
      title: "Remove audience?",
      detail: `Remove "${drafts[idx].name}" from this subscription? Recipients in this audience will stop receiving the report after the next save.`,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
    if (expanded === idx) setExpanded(null);
  }

  const targetKind = detail.subscription.target.kind;

  function addDelivery(): void {
    setDrafts((prev) => [
      ...prev,
      {
        name: `Audience ${prev.length + 1}`,
        contentKind: targetKind === "atlas_report" ? "whole_report" : "whole_page",
        widgetIds: [],
        subjectTemplate: targetKind === "atlas_report" ? "{{report_title}} — {{data_as_of}}" : "{{page_title}} — {{fired_at}}",
        bodyTemplate: "",
        recipients: [],
      },
    ]);
    setExpanded(drafts.length);
  }

  function reset(): void {
    setDrafts(initial);
  }

  const canSave =
    drafts.length > 0 &&
    drafts.every(
      (d) =>
        d.name.trim() &&
        d.subjectTemplate.trim() &&
        (d.contentKind !== "widgets" || d.widgetIds.length > 0) &&
        d.recipients.length > 0 &&
        recipientsAreComplete(d.recipients),
    );

  async function save(): Promise<void> {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        deliveries: drafts.map((d, idx) => ({
          id: d.id,
          name: d.name.trim(),
          content_selection:
            d.contentKind === "whole_report"
              ? { kind: "whole_report" as const }
              : d.contentKind === "whole_page"
                ? { kind: "whole_page" as const }
                : { kind: "widgets" as const, widget_ids: d.widgetIds },
          subject_template: d.subjectTemplate.trim(),
          body_template: d.bodyTemplate.trim() || null,
          sort_order: idx,
          recipients: d.recipients.map(recipientToWire),
        })),
      };
      await apiFetch<{ ok: boolean }>(`/api/tenant/subscriptions/${detail.subscription.id}`, {
        method: "PATCH",
        body: payload,
      });
      await onSaved();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h3>Audiences</h3>
      <div className="help" style={{ marginBottom: 8 }}>
        {targetKind === "atlas_report"
          ? "Each audience receives the same report file with its own subject and body. Drag-free reorder with the ▲ / ▼ buttons."
          : "Each audience receives the same page with its own content selection and subject. Drag-free reorder with the ▲ / ▼ buttons."}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {drafts.map((d, idx) => (
          <DeliveryRow
            key={d.id ?? `new-${idx}`}
            idx={idx}
            total={drafts.length}
            draft={d}
            expanded={expanded === idx}
            targetKind={targetKind}
            widgets={widgets}
            candidates={candidates}
            onToggleExpand={() => setExpanded(expanded === idx ? null : idx)}
            onPatch={(p) => patchDraft(idx, p)}
            onMoveUp={() => moveUp(idx)}
            onMoveDown={() => moveDown(idx)}
            onRemove={() => void removeDelivery(idx)}
          />
        ))}
        <div>
          <button type="button" className="btn" onClick={addDelivery}>
            + Add audience
          </button>
        </div>
      </div>
      <div className="actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn btnPrimary"
          disabled={!dirty || saving || !canSave}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save audiences"}
        </button>
        <button type="button" className="btn" disabled={!dirty || saving} onClick={reset}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function DeliveryRow(props: {
  idx: number;
  total: number;
  draft: DraftDelivery;
  expanded: boolean;
  targetKind: "page" | "atlas_report";
  widgets: PageWidget[];
  candidates: Candidate[];
  onToggleExpand: () => void;
  onPatch: (p: Partial<DraftDelivery>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}): JSX.Element {
  const { idx, total, draft, expanded, targetKind, widgets, candidates, onToggleExpand, onPatch, onMoveUp, onMoveDown, onRemove } =
    props;

  return (
    <div className="card" style={{ border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button
          type="button"
          className="adminListAction"
          disabled={idx === 0}
          onClick={onMoveUp}
          aria-label="Move up"
          title="Move up"
        >
          ▲
        </button>
        <button
          type="button"
          className="adminListAction"
          disabled={idx === total - 1}
          onClick={onMoveDown}
          aria-label="Move down"
          title="Move down"
        >
          ▼
        </button>
        <button
          type="button"
          className="adminListAction"
          onClick={onToggleExpand}
          aria-label="Toggle"
          style={{ fontSize: 24, lineHeight: 1, padding: "4px 12px" }}
        >
          {expanded ? "▾" : "▸"}
        </button>
        <input
          className="input"
          value={draft.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          style={{ flex: 1 }}
        />
        <span className="muted" style={{ fontSize: 12 }}>
          {draft.recipients.length} recipient{draft.recipients.length === 1 ? "" : "s"}
        </span>
        <button type="button" className="adminListAction isDanger" onClick={onRemove} aria-label="Remove audience">
          Remove
        </button>
      </div>
      {expanded ? (
        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          <label className="field">
            <span className="label">Subject</span>
            <input
              className="input"
              value={draft.subjectTemplate}
              onChange={(e) => onPatch({ subjectTemplate: e.target.value })}
            />
            <SubscriptionVariablesHelp target={targetKind} />
          </label>
          <label className="field">
            <span className="label">Body (optional)</span>
            <textarea
              className="input mono"
              rows={4}
              value={draft.bodyTemplate}
              onChange={(e) => onPatch({ bodyTemplate: e.target.value })}
              placeholder="Leave blank for the default body."
            />
          </label>
          {targetKind === "atlas_report" ? (
            <div className="field">
              <span className="label">Content</span>
              <div className="help">The whole report file, as the build produced it.</div>
            </div>
          ) : (
          <div className="field">
            <span className="label">Content</span>
            <label style={{ display: "block", marginTop: 4 }}>
              <input
                type="radio"
                name={`content-${idx}`}
                checked={draft.contentKind === "whole_page"}
                onChange={() => onPatch({ contentKind: "whole_page", widgetIds: [] })}
              />
              <span style={{ marginLeft: 6 }}>Whole page</span>
            </label>
            <label style={{ display: "block", marginTop: 2 }}>
              <input
                type="radio"
                name={`content-${idx}`}
                checked={draft.contentKind === "widgets"}
                onChange={() => onPatch({ contentKind: "widgets" })}
              />
              <span style={{ marginLeft: 6 }}>Specific widgets</span>
            </label>
            {draft.contentKind === "widgets" ? (
              widgets.length === 0 ? (
                <div className="help">No widgets available on this page.</div>
              ) : (
                <div style={{ marginTop: 6, paddingLeft: 16 }}>
                  {widgets.map((w) => (
                    <label key={w.id} style={{ display: "block" }}>
                      <input
                        type="checkbox"
                        checked={draft.widgetIds.includes(w.id)}
                        onChange={(e) =>
                          onPatch({
                            widgetIds: e.target.checked
                              ? [...draft.widgetIds, w.id]
                              : draft.widgetIds.filter((id) => id !== w.id),
                          })
                        }
                      />
                      <span style={{ marginLeft: 6 }}>
                        {w.title}{" "}
                        <span className="muted" style={{ fontSize: 12 }}>
                          ({w.kind})
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )
            ) : null}
          </div>
          )}
          <RecipientsEditor
            recipients={draft.recipients}
            candidates={candidates}
            onChange={(recipients) => onPatch({ recipients })}
          />
        </div>
      ) : null}
    </div>
  );
}

